import React, { useEffect, useRef, useState } from "react";
import "./WhatsAppFloat.css";

const WHATSAPP_NUMBER = "918796556740";
const WHATSAPP_MESSAGE = "Hi, I have a query about BookTalent. Please help me.";
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`;
const POSITION_KEY = "bt_whatsapp_float_position";
const DRAG_THRESHOLD = 6; // px of movement before a press counts as a drag, so taps still open WhatsApp
const EDGE_GAP = 8;

const readSavedPosition = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(POSITION_KEY) || "null");
    return saved && Number.isFinite(saved.x) && Number.isFinite(saved.y) ? saved : null;
  } catch {
    return null;
  }
};

export default function WhatsAppFloat() {
  const wrapRef = useRef(null);
  const dragRef = useRef(null);
  const justDraggedRef = useRef(false);
  const [position, setPosition] = useState(readSavedPosition);
  const [dragging, setDragging] = useState(false);

  // Keep the button fully on screen.
  const clamp = (x, y) => {
    const el = wrapRef.current;
    const width = el?.offsetWidth || 0;
    const height = el?.offsetHeight || 0;
    return {
      x: Math.min(Math.max(EDGE_GAP, x), window.innerWidth - width - EDGE_GAP),
      y: Math.min(Math.max(EDGE_GAP, y), window.innerHeight - height - EDGE_GAP),
    };
  };

  const hasPosition = Boolean(position);
  useEffect(() => {
    if (!hasPosition) return undefined;
    const keepInView = () => setPosition((current) => (current ? clamp(current.x, current.y) : current));
    keepInView();
    window.addEventListener("resize", keepInView);
    return () => window.removeEventListener("resize", keepInView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPosition]);

  const onPointerDown = (event) => {
    if (event.button !== 0) return;
    const rect = wrapRef.current.getBoundingClientRect();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      offsetX: event.clientX - rect.left,
      offsetY: event.clientY - rect.top,
      active: false,
    };
  };

  const onPointerMove = (event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    if (!drag.active) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) < DRAG_THRESHOLD) return;
      drag.active = true;
      setDragging(true);
      // Capture only once it is a real drag; capturing on press would swallow normal clicks.
      try { wrapRef.current.setPointerCapture(event.pointerId); } catch { /* pointer already released */ }
    }
    setPosition(clamp(event.clientX - drag.offsetX, event.clientY - drag.offsetY));
  };

  const endDrag = () => {
    const drag = dragRef.current;
    dragRef.current = null;
    if (!drag?.active) return;
    justDraggedRef.current = true;
    setDragging(false);
    setPosition((current) => {
      try { localStorage.setItem(POSITION_KEY, JSON.stringify(current)); } catch { /* storage unavailable */ }
      return current;
    });
  };

  // A drag must not also open WhatsApp.
  const onClickCapture = (event) => {
    if (!justDraggedRef.current) return;
    justDraggedRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      ref={wrapRef}
      className={`whatsapp-float-wrap ${dragging ? "is-dragging" : ""}`}
      style={position ? { left: position.x, top: position.y, right: "auto", bottom: "auto" } : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
      title="Drag to move"
    >
      <a
        className="whatsapp-float"
        draggable={false}
        onDragStart={(event) => event.preventDefault()}
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Have a query, doubt, or concern? Ask us on WhatsApp"
        data-testid="whatsapp-floating-button"
      >
        <span className="whatsapp-float-copy">
          <span className="whatsapp-float-heading">Have any query?</span>
          <span className="whatsapp-float-message">Any doubt or concern? Ask here.</span>
        </span>
        <span className="whatsapp-float-icon" aria-hidden="true">
          <svg viewBox="0 0 32 32" focusable="false">
            <path
              fill="currentColor"
              d="M16 3.2A12.6 12.6 0 0 0 5.2 22.3L3.5 28.5l6.4-1.7A12.6 12.6 0 1 0 16 3.2Zm0 22.8c-2 0-3.9-.5-5.6-1.6l-.4-.2-3.8 1 1-3.7-.3-.4A10.1 10.1 0 1 1 16 26Z"
            />
            <path
              fill="currentColor"
              d="M22.1 18.7c-.3-.2-1.9-.9-2.2-1s-.5-.2-.7.2-.8 1-1 1.2-.4.2-.7.1a8.4 8.4 0 0 1-2.5-1.5 9.3 9.3 0 0 1-1.7-2.1c-.2-.3 0-.5.1-.6l.5-.6.3-.5c.1-.2 0-.4 0-.5s-.7-1.7-1-2.3c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1.1 1.1-1.1 2.7s1.1 3.1 1.3 3.3c.2.2 2.2 3.4 5.3 4.7.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.9-.8 2.2-1.5.3-.8.3-1.4.2-1.5-.1-.2-.3-.3-.6-.5Z"
            />
          </svg>
        </span>
        <span className="whatsapp-float-badge" aria-hidden="true">ASK US</span>
      </a>
    </div>
  );
}
